/**
 * @vuelto-surface
 * mobile: ok
 * desktop: ok
 * strategy: unified
 */
import React, {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react';
import { Platform, StyleSheet, Text, TouchableOpacity, View, Vibration } from 'react-native';
import { CommonActions } from '@react-navigation/native';
import { ticketColors, ticketFonts } from '../../shared/theme/ticketColors';
import { spacing } from '../../shared/theme/spacing';
import { usePendingOrdersCount } from './PendingOrdersCountProvider';
import { usePublicStoreFeaturesVisible } from '../feature-flags/usePublicStoreFeaturesVisible';
import { useHasBusinessCapability } from '../session/hooks/useEffectiveBusinessCapabilities';
import { getRootNavigationRef } from '../navigation/shellNavigationReset';
import { registerForPushNotificationsAsync, setupNotificationListeners } from '../../shared/notifications/pushTokenService';
import { useAuthStore } from '../../auth/store/useAuthStore';
import { canRunOperationalBackgroundWork } from '../session/accountSetupService';
import { isOnlineOrderSoundEnabled } from '../../onboarding/application/onboardingUiStorage';

// Suprimir la alerta del sistema cuando la app está en foreground (nativo).
// En web, expo-notifications no aplica y su import dispara warnings.
if (Platform.OS !== 'web') {
  void import('expo-notifications').then((Notifications) => {
    Notifications.setNotificationHandler({
      handleNotification: () =>
        Promise.resolve({
          shouldShowAlert: false,
          shouldPlaySound: false,
          shouldSetBadge: false,
          shouldShowBanner: false,
          shouldShowList: false,
          priority: Notifications.AndroidNotificationPriority.MAX,
        }),
    });
  });
}

function navigateRootToOrdersInbox(): void {
  const nav = getRootNavigationRef();
  if (!nav?.isReady?.()) return;
  nav.dispatch(
    CommonActions.navigate({
      name: 'AppShell',
      params: {
        screen: 'MainTabs',
        params: { screen: 'OrdersInbox' },
      },
    })
  );
}

function navigateRootToAgenda(appointmentId?: string, startsAt?: string): void {
  const nav = getRootNavigationRef();
  if (!nav?.isReady?.()) return;
  nav.dispatch(
    CommonActions.navigate({
      name: 'AppShell',
      params: {
        screen: 'MainTabs',
        params: { screen: 'Appointments', params: { focusAppointmentId: appointmentId, focusDate: startsAt } },
      },
    })
  );
}

type NewOrderAlertContextValue = {
  /** Dispara alerta (p. ej. desde push foreground). */
  ring: (opts?: { orderId?: string; title?: string; body?: string }) => void;
  dismiss: () => void;
  active: boolean;
};

const NewOrderAlertContext = createContext<NewOrderAlertContextValue | null>(null);

function playBeep() {
  try {
    if (Platform.OS === 'web' && typeof window !== 'undefined') {
      const Ctx = (window as any).AudioContext || (window as any).webkitAudioContext;
      if (!Ctx) return;
      const ctx = new Ctx();
      const osc = ctx.createOscillator();
      const gain = ctx.createGain();
      osc.type = 'square';
      osc.frequency.value = 880;
      gain.gain.value = 0.08;
      osc.connect(gain);
      gain.connect(ctx.destination);
      osc.start();
      setTimeout(() => {
        osc.stop();
        void ctx.close();
      }, 180);
      setTimeout(() => {
        const ctx2 = new Ctx();
        const osc2 = ctx2.createOscillator();
        const gain2 = ctx2.createGain();
        osc2.type = 'square';
        osc2.frequency.value = 1175;
        gain2.gain.value = 0.08;
        osc2.connect(gain2);
        gain2.connect(ctx2.destination);
        osc2.start();
        setTimeout(() => {
          osc2.stop();
          void ctx2.close();
        }, 220);
      }, 220);
      return;
    }
    Vibration.vibrate([0, 200, 100, 200]);
  } catch {
    // ignore audio failures
  }
}

function AlertBanner({
  title,
  body,
  onOpen,
  onDismiss,
}: {
  title: string;
  body: string;
  onOpen: () => void;
  onDismiss: () => void;
}) {
  return (
    <View
      style={[styles.banner, Platform.OS === 'web' ? styles.bannerWeb : null]}
      accessibilityRole="alert"
      pointerEvents="auto"
    >
      <View style={styles.bannerText}>
        <Text style={styles.bannerTitle}>{title}</Text>
        <Text style={styles.bannerBody} numberOfLines={2}>
          {body}
        </Text>
      </View>
      <TouchableOpacity style={styles.openBtn} onPress={onOpen} accessibilityRole="button">
        <Text style={styles.openBtnText}>Ver</Text>
      </TouchableOpacity>
      <TouchableOpacity style={styles.dismissBtn} onPress={onDismiss} accessibilityRole="button" accessibilityLabel="Cerrar">
        <Text style={styles.dismissBtnText}>✕</Text>
      </TouchableOpacity>
    </View>
  );
}

/**
 * Timbre de pedido nuevo: detecta suba de pendientes + push, banner sticky + beep/vibración.
 */
export function NewOrderAlertProvider({ children }: { children: React.ReactNode }) {
  const { count, refresh } = usePendingOrdersCount();
  const publicStoreOn = usePublicStoreFeaturesVisible();
  const hasAppointments = useHasBusinessCapability('appointments');
  const shouldAcceptAlerts = publicStoreOn || hasAppointments;
  const prevCount = useRef<number | null>(null);
  const [active, setActive] = useState(false);
  const [title, setTitle] = useState('Pedido nuevo');
  const [body, setBody] = useState('Hay pedidos pendientes de aceptar.');
  const loopRef = useRef<ReturnType<typeof setInterval> | null>(null);

  const stopLoop = useCallback(() => {
    if (loopRef.current) {
      clearInterval(loopRef.current);
      loopRef.current = null;
    }
  }, []);

  const dismiss = useCallback(() => {
    setActive(false);
    stopLoop();
  }, [stopLoop]);

  const ring = useCallback(
    (opts?: { orderId?: string; title?: string; body?: string }) => {
      if (!shouldAcceptAlerts) return;
      setTitle(opts?.title ?? 'Pedido nuevo');
      setBody(opts?.body ?? 'Hay pedidos pendientes de aceptar.');
      setActive(true);
      void (async () => {
        const uid = useAuthStore.getState().session?.user?.id;
        if (!uid || !(await isOnlineOrderSoundEnabled(uid))) return;
        playBeep();
        stopLoop();
        loopRef.current = setInterval(() => playBeep(), 4000);
      })();
    },
    [shouldAcceptAlerts, stopLoop],
  );

  useEffect(() => {
    if (!publicStoreOn || count == null) return;
    if (prevCount.current != null && count > prevCount.current) {
      ring({
        title: count === 1 ? '1 pedido nuevo' : `${count} pedidos pendientes`,
        body: 'Abrilo con Ver para aceptarlo.',
      });
    }
    prevCount.current = count;
  }, [count, publicStoreOn, ring]);

  // Sin poll propio: el badge lo refresca PendingOrdersCountProvider via
  // realtime en customer_orders (+ fallback de 60s) y el count>prev dispara ring.

  const userId = useAuthStore((s) => s.session?.user?.id);
  useEffect(() => {
    if (!userId || !shouldAcceptAlerts || !canRunOperationalBackgroundWork()) return;
    let cleanup: (() => void) | null = null;
    void (async () => {
      if (Platform.OS === 'android') {
        const Notifications = await import('expo-notifications');
        await Notifications.setNotificationChannelAsync('new-orders', {
          name: 'Pedidos y turnos',
          importance: Notifications.AndroidImportance.MAX,
          vibrationPattern: [0, 250, 150, 250],
        });
      }
      await registerForPushNotificationsAsync();
      cleanup = setupNotificationListeners(
        (t, b) => {
          ring({ title: t || 'Pedido nuevo', body: b || 'Hay un pedido pendiente.' });
          void refresh({ force: true });
        },
        (data) => {
          dismiss();
          if (data?.screen === 'Agenda' || data?.type === 'appointment') {
            navigateRootToAgenda(data?.appointment_id, data?.starts_at);
          } else {
            navigateRootToOrdersInbox();
          }
        }
      );
    })();
    return () => cleanup?.();
  }, [userId, shouldAcceptAlerts, ring, dismiss, refresh]);

  useEffect(() => () => stopLoop(), [stopLoop]);

  const value = useMemo(() => ({ ring, dismiss, active }), [ring, dismiss, active]);

  return (
    <NewOrderAlertContext.Provider value={value}>
      {children}
      {active ? (
        <NewOrderAlertHost title={title} body={body} onDismiss={dismiss} />
      ) : null}
    </NewOrderAlertContext.Provider>
  );
}

function NewOrderAlertHost({
  title,
  body,
  onDismiss,
}: {
  title: string;
  body: string;
  onDismiss: () => void;
}) {
  const open = () => {
    onDismiss();
    navigateRootToOrdersInbox();
  };
  return (
    <View style={styles.host} pointerEvents="box-none">
      <AlertBanner title={title} body={body} onOpen={open} onDismiss={onDismiss} />
    </View>
  );
}

export function useNewOrderAlert(): NewOrderAlertContextValue {
  const ctx = useContext(NewOrderAlertContext);
  if (!ctx) {
    return {
      ring: () => undefined,
      dismiss: () => undefined,
      active: false,
    };
  }
  return ctx;
}

const styles = StyleSheet.create({
  host: {
    position: 'absolute',
    left: 0,
    right: 0,
    top: Platform.OS === 'web' ? 8 : 48,
    zIndex: 9999,
    paddingHorizontal: spacing.md,
    // Web: la alerta es un toast en la esquina — sin alignItems el banner se
    // estira a todo el ancho y tapa los controles del header (POS/Caja).
    ...(Platform.OS === 'web' ? { alignItems: 'flex-end' as const } : null),
  },
  banner: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: spacing.sm,
    paddingVertical: 12,
    paddingHorizontal: spacing.md,
    borderRadius: 10,
    backgroundColor: ticketColors.gold,
    borderWidth: 1,
    borderColor: ticketColors.gold,
  },
  bannerWeb: {
    maxWidth: 440,
    minWidth: 300,
    alignSelf: 'flex-end',
  },
  bannerText: { flex: 1, minWidth: 0 },
  bannerTitle: {
    fontSize: 15,
    fontWeight: '800',
    color: ticketColors.goldInk,
    fontFamily: ticketFonts.sans,
  },
  bannerBody: {
    fontSize: 12,
    color: ticketColors.goldInk,
    opacity: 0.9,
    fontFamily: ticketFonts.sans,
  },
  openBtn: {
    backgroundColor: ticketColors.paper,
    paddingHorizontal: 14,
    paddingVertical: 8,
    borderRadius: 8,
  },
  openBtnText: {
    fontWeight: '800',
    color: ticketColors.gold,
    fontSize: 13,
  },
  dismissBtn: { padding: 6 },
  dismissBtnText: { color: ticketColors.goldInk, fontSize: 16, fontWeight: '700' },
});
